/**
 * Page metadata for the Staff Permissions feature.
 * `key` is the canonical identifier used in the DB matrix and in the `can()` helper.
 * `route` is the path the sidebar / route guard uses to gate access.
 * `hasEdit` / `hasDelete` mark whether those rights are meaningful for the page;
 *    cells render as a non-interactive dash when false.
 *
 * Order here = order in the Settings matrix rows.
 */
export const PAGES = [
  { key: 'dashboard',     label: 'Dashboard',     route: '/dashboard',       hasEdit: false, hasDelete: false },
  { key: 'sales',         label: 'Sales',         route: '/invoices',        hasEdit: true,  hasDelete: true  },
  { key: 'customer',      label: 'Customer',      route: '/customers',       hasEdit: true,  hasDelete: true  },
  { key: 'services',      label: 'Services',      route: '/services',        hasEdit: true,  hasDelete: true  },
  { key: 'followup',      label: 'Follow-Up',     route: '/processing',      hasEdit: true,  hasDelete: false },
  { key: 'notes',         label: 'To-Do Notes',   route: '/notes',           hasEdit: true,  hasDelete: true  },
  { key: 'transfer',      label: 'Transfer',      route: '/transfer',        hasEdit: true,  hasDelete: true  },
  { key: 'expense',       label: 'Payment',       route: '/expense',         hasEdit: true,  hasDelete: true  },
  { key: 'income',        label: 'Income',        route: '/income',          hasEdit: true,  hasDelete: true  },
  { key: 'balance_sheet', label: 'Balance Sheet', route: '/finance',         hasEdit: true,  hasDelete: true  },
  { key: 'reports',       label: 'Reports',       route: '/reports',         hasEdit: false, hasDelete: false },
  // Feature toggle — allows staff to assign invoice items to other staff
  // (the "Assigned To" dropdown on Create/Edit Invoice). Off by default.
  // Admin always has it implicitly. Only `view` is meaningful here.
  { key: 'work_assign',   label: 'Work Assign',   route: null,               hasEdit: false, hasDelete: false },
  // Feature toggle — show Billed / Due (Svc · Govt) totals on the Sales
  // page's daily & monthly summary rows. Only `view` is meaningful.
  { key: 'sales_summary', label: 'Sales Summary (Billed/Due)', route: null,   hasEdit: false, hasDelete: false },
  // --- Staff Management module (Phase 1: shipped → Shift Settings; rest in later phases) ---
  { key: 'attendance',           label: 'Attendance',           route: '/staff/attendance', hasEdit: true,  hasDelete: true  },
  { key: 'leave',                label: 'Leave',                route: '/staff/leave',      hasEdit: true,  hasDelete: true  },
  { key: 'salary',               label: 'Salary',               route: '/staff/salary',     hasEdit: true,  hasDelete: false },
  { key: 'staff_performance',    label: 'Staff Performance',    route: '/staff/performance',hasEdit: false, hasDelete: false },
  { key: 'work_log',             label: 'Work Log',             route: '/staff/work-log',   hasEdit: true,  hasDelete: false },
  { key: 'shift_settings',       label: 'Shift Settings',       route: '/staff/shifts',     hasEdit: true,  hasDelete: false },
  { key: 'incentives_deductions',label: 'Incentives/Deductions',route: '/staff/incentives', hasEdit: true,  hasDelete: true  },
  { key: 'staff_reports',        label: 'Staff Reports',        route: '/staff/reports',    hasEdit: false, hasDelete: false },
  // Daily Transaction Report — admin-only by default.
  { key: 'daily_report',         label: 'Daily Transaction Report', route: '/reports/daily', hasEdit: false, hasDelete: false },
];

// Map sidebar nav items to permission keys (Layout.js uses `to` paths).
// Pages without a route (feature-toggle pseudo-pages like `work_assign`) are skipped.
export const ROUTE_TO_PAGE = PAGES.reduce((acc, p) => { if (p.route) acc[p.route] = p.key; return acc; }, {});
// Receipt entry isn't a standalone permission — it edits invoice payments,
// so it gates on the `sales` permission key. Mapped here so the sidebar
// filter still works without polluting the matrix UI.
ROUTE_TO_PAGE['/receipt'] = 'sales';
// Recurring page reuses the `sales` permission key — it surfaces existing
// invoices and the Renew action that creates a new invoice. No separate
// permission row to keep the matrix tidy.
ROUTE_TO_PAGE['/recurring'] = 'sales';
// My Work is universal — every staff member sees only their own assigned
// items, so we don't gate it behind a permission key. Map it to a dummy
// 'self' that the useCan() helper short-circuits below.
ROUTE_TO_PAGE['/my-work'] = '__self__';

/** Pure helper: returns whether the given permissions matrix allows `action` on `page`. */
export const can = (perms, page, action = 'view') => {
  if (!perms) return false;
  return Boolean(perms[page]?.[action]);
};

/** Hook variant: reads permissions from AuthContext. Admin always returns true. */
import { useAuth } from '@/context/AuthContext';
export function useCan(page, action = 'view') {
  const { user, permissions } = useAuth();
  if (!user) return false;
  if (user.role === 'admin') return true;
  // `__self__` sentinel — any logged-in user has access (used for /my-work).
  if (page === '__self__') return true;
  return can(permissions, page, action);
}

/** Find the first page the user is allowed to view — used for fallback redirects. */
export const firstAllowedRoute = (perms) => {
  if (!perms) return null;
  const found = PAGES.find(p => p.route && perms[p.key]?.view);
  return found ? found.route : null;
};
